import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import React, {Component, ReactElement} from 'react'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {ResidentLoginInfoSessionStorageKey} from '../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {ASSOC_DIRECTOR_FLAG, ASSOC_GROUP, VALID_OR_NOT} from '../../../CodeMaster'
import NatResidentUserRepo from '../residentUser/repository/NatResidentUserRepo'
import {BlockSpacer, currentMedia} from '../../../styledComponents/ResponsiveConvenience'
import styled from 'styled-components'
import {StyledPageTitle} from '../../../components/reservations/components/CommonStyle'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {
    FlexContainer,
    FullWidthColumnFlexContainer,
} from '../../../styledComponents/FlexComponents'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {PlainStyledButton} from '../../../styledComponents/Button'
import {Box} from '@mui/material'
import {Nullable} from '../../../app/TypeHelpers'
import moment from 'moment'
import {PlainForwardLinkStyledButton} from '../AmountFix/common/BillingDataCommon'
import {faFileLines} from '@fortawesome/free-regular-svg-icons/faFileLines'
import {judgeAuthorityCheck} from '../../common/util/Natutil'
import NatDisasterRepo from './repository/NatDisasterRepo'
import {ListGridContainer} from '../../../styledComponents/GridComponents';

interface Props {
    condominiumCd: string,
    residentUserRepo: NatResidentUserRepo,
    disasterRepo: NatDisasterRepo,
}

interface State {
    isLoadCompleted: boolean,
    isPosition: boolean,
    isDisasterMode: boolean,
    disasterEndDate?: string,
}

class NatResidentDisasterPreventionPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    isAssocDirector: boolean

    constructor(props: Readonly<Props & ResidentWithErrorHandlingPagePropsSet>) {
        super(props)
        this.state = {
            isLoadCompleted: false,
            isPosition: false,
            isDisasterMode: false,
        }
        this.isAssocDirector = SessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.ASSOC_DIRECTOR_FLAG)
            === ASSOC_DIRECTOR_FLAG.TRUE.value

        this.confirmDisasterPreventButtonClick = this.confirmDisasterPreventButtonClick.bind(this);
        this.infoSharingButtonClick = this.infoSharingButtonClick.bind(this);
        this.disasterModeChangeLinkClick = this.disasterModeChangeLinkClick.bind(this);
    }

    async componentDidMount(): Promise<void> {
        const {residentUserRepo, disasterRepo, eventEmitter} = this.props
        await this.props.eventEmitter.showLoading();
        try {
            // 管理組合役員の場合、役職情報を取得する
            if (this.isAssocDirector) {
                const residentUserResponse = await this.props.asyncCallFunction(
                    residentUserRepo.getResidentUserLogined.bind(residentUserRepo),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    }
                )
                if (residentUserResponse?.details?.positionInfo) {
                    const authorizedPositions = [
                        ASSOC_GROUP.ASSOC_CHAIRMAN.value, ASSOC_GROUP.ASSOC_VICE_CHAIRMAN.value
                    ]
                    const isPosition = residentUserResponse.details.positionInfo
                        .some(position => authorizedPositions.includes(position.positionRoleId)) ?? false
                    this.setState({isPosition: isPosition});
                }
            }

            // 災害時モード設定を取得する
            if (this.props.condominiumCd) {
                const disasterModeSetting = await this.props.asyncCallFunction(
                    disasterRepo.getDisasterMode.bind(disasterRepo, this.props.condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    }
                )
                if (disasterModeSetting?.mode === VALID_OR_NOT.VALID.value) {
                    this.setState({
                        isDisasterMode: true,
                        disasterEndDate: disasterModeSetting.disasterEndDate ?? ''
                    })
                }
            }
        } finally {
            this.props.eventEmitter.closeLoading();
            this.setState({
                isLoadCompleted: true
            })
        }
    }

    disasterModeChangeLinkClick(): void {
        if (this.state.isDisasterMode) {
            this.props.navigate(
                `/resident/condominiums/${this.props.condominiumCd}/disasters/mode/releaseExtension`,
            )
        } else {
            this.props.navigate(
                `/resident/condominiums/${this.props.condominiumCd}/disasters/mode/setting`,
            )
        }
    }

    confirmDisasterPreventButtonClick(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/disasters/preventions`,
        )
    }

    infoSharingButtonClick(): void {
        this.props.navigate(`/resident/condominiums/${this.props.condominiumCd}/disasters/topics`,
        )
    }

    // 災害時モードラベル
    renderDisasterMode(): Nullable<ReactElement> {
        if (!this.state.isDisasterMode) {
            return null
        }
        return (
            <DisasterModeLabel>
                <DisasterModeLabelTextBlock>
                    <DisasterModeLabelText>
                        災害時モード
                    </DisasterModeLabelText>
                    {this.state.disasterEndDate && this.state.isPosition ?
                        <DisasterModeLabelDateText>
                            {moment(this.state.disasterEndDate).format('YYYY年M月D日(dd)') + 'まで'}
                        </DisasterModeLabelDateText>
                        : ''
                    }
                </DisasterModeLabelTextBlock>
            </DisasterModeLabel>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (<>
            {this.renderDisasterMode()}
            <BlockSpacer height={'15px'}/>
            <MainContainer>
                <StyledTitle>防災たすけあい</StyledTitle>
                <MainTextBlock>
                    このマンションの防災情報を確認できます。<br/>
                    災害発生時には、「災害時モード」となり、防災情報の確認に加えて、このマンションの素敵ネット利用者と情報共有を行う機能を利用することができます。
                    <BlockSpacer height={'10px'}/>
                    <ListGridContainer fontSize={FontSize.Middle}>
                        <div>・</div>
                        <Box pl={'5px'}>災害時モードの設定や解除は、理事長・副理事長ならびに管理会社の判断で行われます。（この操作は、理事長・副理事長のみ行うことができます。）</Box>
                        <div>・</div>
                        <Box pl={'5px'}>災害時モードが解除されると、情報共有を行う機能は利用できなくなります。</Box>
                    </ListGridContainer>
                </MainTextBlock>
            </MainContainer>
            <BlockSpacer height={'20px'}/>
            {this.state.isPosition &&
                <StyledLinkArea>
                    <StyledLink>
                        <PlainForwardLinkStyledButton
                            onClick={this.disasterModeChangeLinkClick}>
                            災害時モード設定
                        </PlainForwardLinkStyledButton>
                    </StyledLink>
                </StyledLinkArea>}
            <ButtonContainer>
                <StyledButton
                    disabled={!judgeAuthorityCheck('VIEW-DISASTER_PREVENTION')}
                    onClick={this.confirmDisasterPreventButtonClick}>
                    <FontAwesomeIcon icon={faFileLines} color={Colors.Primary}
                                     fontSize={'40px'} width={'40px'}/>
                    <Box pl={'20px'}>防災情報を確認する</Box>
                </StyledButton>
                <StyledButton
                    disabled={!(judgeAuthorityCheck('VIEW-DISASTER_INFORMATION_SHARING') && this.state.isDisasterMode)}
                    onClick={this.infoSharingButtonClick}>
                    <StyledIconImage src={`/resources/images/icons/icon_connect_without_contact.svg`}
                                     alt="居住者同士で情報共有を行う"/>
                    <Box pl={'20px'}>居住者同士で情報共有を行う</Box>
                </StyledButton>
            </ButtonContainer>
            <BlockSpacer height={'15px'}/>
        </>)
    }

}

export default createResidentWithErrorHandlingPage(NatResidentDisasterPreventionPage)

const DisasterModeLabel = styled.div`
    position: relative;
    width: calc(100% + 30px);
    margin: 0px -15px 15px -15px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${Colors.Disaster};
`
const DisasterModeLabelTextBlock = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px 15px;
    gap: 10px
`
const DisasterModeLabelText = styled.span`
    font-weight: ${FontWeights.Bold};
    font-size: ${FontSize.Body};
    color: ${Colors.Surface};
    line-height: 24px;
`
const DisasterModeLabelDateText = styled.span`
    font-weight: ${FontWeights.Bold};
    font-size: ${FontSize.Body};
    color: ${Colors.Surface};
    line-height: 24px;
`
const MainContainer = styled(FullWidthColumnFlexContainer)`
    gap: 20px;
`
const StyledTitle = styled(StyledPageTitle)`
    width: 100%;
    text-align: left;
`
const MainTextBlock = styled.div`
    width: 738px;
    ${currentMedia.lessThan('sp')`
    width:100%;
  `}
`
const StyledLinkArea = styled.div`
    width: 738px;
    margin: auto;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    ${currentMedia.lessThan('sp')`
    width:100%;
    max-width: 345px;
  `}
`
const StyledLink = styled.div`
    padding: 0px 10px 15px 15px;
`
const ButtonContainer = styled(FlexContainer)`
    flex-direction: row;
    width: 738px;
    margin: auto;
    gap: 30px;
    align-items: center;
    justify-content: center;
    ${currentMedia.lessThan('sp')`
    flex-direction: column;
    width: 100%;
    gap: 20px;
  `}
`
const StyledButton = styled(PlainStyledButton)`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 100px;
    width: 354px;
    padding: 14px 24px;

    font-size: ${FontSize.Body};
    font-weight: ${FontWeights.Bold};
    border: none;
    background: ${Colors.Surface};
    box-shadow: 0px 1px 8px 0px rgba(112, 112, 112, 0.4);
    border-radius: 8px;
    color: ${Colors.Body};

    :active {
        background: linear-gradient(0deg, rgba(112, 112, 112, 0.75), rgba(112, 112, 112, 0.75)), #D9D9D9;
        box-shadow: none;
    }

    :disabled {
        pointer-events: none;
        background-color: ${Colors.AccentLight};
    }

    ${currentMedia.lessThan('sp')`
    width:345px;
  `}
`
const StyledIconImage = styled.img`
    width: 40px;
    height: 40px;
`